import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { snapToTimeVals } from '@aytm/app/stats/page/question-views/emd/lib/state'
import { resolveThumbSrc } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/question-views/emd/components/video/styles/Video.module.scss'

// Cap ported verbatim from the coffee video template: the frame never grows past 470px tall.
const MAX_HEIGHT = 470

const formatTime = seconds => {
  if (!isFinite(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Native <video> with a custom control bar. Replaces the video.js-backed coffee VideoView.
// The Backbone model used to broker videoTime/playing between the video and the chart; that
// role now belongs to the parent block's reducer, so this component only reports its own
// playback (onTimeChange / onPlayingChange) and reacts to an externally-driven videoTime
// (chart hover / seek) when it is paused — matching the legacy `setVideoTime` paused-guard.
const Video = ({
  video,
  width,
  videoTime,
  timeVals,
  onTimeChange,
  onPlayingChange,
  viewMode,
  lineMode,
  containerRef
}) => {
  const videoRef = useRef(null)
  const wrapRef = useRef(null)

  // Stable merged ref: keep an internal handle (for fullscreen) and forward the node to the
  // parent's callback ref so the block can hand this container to the chart as `videoEl`.
  const setRefs = useCallback(
    node => {
      wrapRef.current = node
      if ('function' === typeof containerRef) containerRef(node)
      else if (containerRef) containerRef.current = node
    },
    [containerRef]
  )

  const ratio = video?.ratio || 0.5
  let frameWidth = width
  let frameHeight = frameWidth * ratio
  if (frameHeight > MAX_HEIGHT) {
    frameHeight = MAX_HEIGHT
    frameWidth = frameHeight / ratio
  }

  const thumbnailUrl = resolveThumbSrc(video)

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(videoTime || 0)
  const [duration, setDuration] = useState(video?.duration || 0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)

  const togglePlay = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    if (el.paused) el.play()
    else el.pause()
  }, [])

  const handleSeek = useCallback(event => {
    const el = videoRef.current
    if (el) el.currentTime = Number(event.target.value)
  }, [])

  const handleVolume = useCallback(event => {
    const el = videoRef.current
    const value = Number(event.target.value)
    setVolume(value)
    setMuted(0 === value)
    if (el) {
      el.volume = value
      el.muted = 0 === value
    }
  }, [])

  const toggleMute = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    const next = !el.muted
    el.muted = next
    setMuted(next)
  }, [])

  const toggleFullscreen = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen?.()
    else el.requestFullscreen?.()
  }, [])

  // Own playback events. The snapping (paused/seeking only) mirrors the legacy timeupdate
  // handler: during normal playback the chart animation owns the position, so we don't snap.
  useEffect(() => {
    const el = videoRef.current
    if (!el) return

    const onPlay = () => {
      setPlaying(true)
      onPlayingChange?.(true)
    }
    const onPause = () => {
      setPlaying(false)
      onPlayingChange?.(false)
    }
    const onWaiting = () => onPlayingChange?.(false)
    // a stall resumes on its own, announced by `playing` and never by a new `play`, so the chart's
    // cursor, stopped by the stall, starts again here rather than staying where it stopped
    const onResumed = () => onPlayingChange?.(true)
    const onEnded = () => {
      setPlaying(false)
      onPlayingChange?.(false)
      el.currentTime = 0
    }
    const onLoadedMeta = () => setDuration(el.duration || 0)
    const onTimeUpdate = () => {
      setCurrentTime(el.currentTime)
      if (el.paused || el.seeking) {
        const snapped = snapToTimeVals(el.currentTime, timeVals)
        if (snapped !== videoTime) onTimeChange?.(snapped)
      }
    }

    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('waiting', onWaiting)
    el.addEventListener('playing', onResumed)
    el.addEventListener('ended', onEnded)
    el.addEventListener('loadedmetadata', onLoadedMeta)
    el.addEventListener('timeupdate', onTimeUpdate)

    return () => {
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('waiting', onWaiting)
      el.removeEventListener('playing', onResumed)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('loadedmetadata', onLoadedMeta)
      el.removeEventListener('timeupdate', onTimeUpdate)
    }
  }, [timeVals, videoTime, onTimeChange, onPlayingChange])

  // Externally-driven videoTime (chart hover / seed) seeks the scrubber only while paused —
  // during playback the chart animation drives videoTime and must not fight the video clock.
  useEffect(() => {
    const el = videoRef.current
    if (!el || !el.paused) return
    if (Math.abs(el.currentTime - videoTime) > 0.001) el.currentTime = videoTime
  }, [videoTime])

  // Switching view/line mode pauses playback (legacy `videoPause` on change:viewMode/lineMode).
  useEffect(() => {
    videoRef.current?.pause()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, lineMode])

  if (!video) {
    return (
      <div className={style.missing}>
        <img src="/images/external_survey/missing_video.png" alt="" />
      </div>
    )
  }

  return (
    <div className={style.component} ref={setRefs} style={{ width: `${frameWidth}px` }}>
      <video
        ref={videoRef}
        className={style.video}
        width={frameWidth}
        height={frameHeight}
        poster={thumbnailUrl}
        preload="auto"
        src={video.mp4File}
        onClick={togglePlay}
      />
      <div className={style.controls}>
        <i
          className={classNames(playing ? 'icon-aytmfd-pause' : 'icon-aytmfd-play', style.button)}
          aria-label={playing ? 'Pause' : 'Play'}
          {...pressable(togglePlay)}
        />
        <span className={style.time}>{formatTime(currentTime)}</span>
        <input
          className={classNames(style.scrubber, { [style.disabled]: playing })}
          aria-label="Seek"
          type="range"
          min={0}
          max={duration || 0}
          step="any"
          value={currentTime}
          disabled={playing}
          onChange={handleSeek}
        />
        <span className={style.time}>{formatTime(duration)}</span>
        <i
          className={classNames(muted ? 'icon-aytmfd-nosound' : 'pi pi-volume-up', style.button)}
          aria-label="Mute"
          aria-pressed={muted}
          {...pressable(toggleMute)}
        />
        <input
          className={style.volume}
          aria-label="Volume"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={muted ? 0 : volume}
          onChange={handleVolume}
        />
        <i
          className={classNames('icon-aytmfd-full-screen', style.button)}
          aria-label="Full screen"
          {...pressable(toggleFullscreen)}
        />
      </div>
    </div>
  )
}

Video.propTypes = {
  video: PropTypes.object,
  width: PropTypes.number,
  videoTime: PropTypes.number,
  timeVals: PropTypes.array,
  onTimeChange: PropTypes.func,
  onPlayingChange: PropTypes.func,
  viewMode: PropTypes.string,
  lineMode: PropTypes.string,
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
}

Video.defaultProps = {
  videoTime: 0,
  timeVals: []
}

Video.displayName = 'EmotionalDialVideo'

export default Video
